import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FaBan, FaCheckCircle, FaSyncAlt } from 'react-icons/fa';
import { AuthContext } from '../contexts/AuthContext';
import { buildApiUrl, buildAuthHeaders } from '../config/api';
import './SoldOutPage.css';

function SoldOutPage() {
    const { user } = useContext(AuthContext);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [error, setError] = useState('');

    const loadMenu = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(buildApiUrl('/api/menu'));
            if (!response.ok) throw new Error('Unable to load menu availability.');
            setMenuItems(await response.json());
            setError('');
        } catch (requestError) {
            setError(requestError.message || 'Unable to load menu availability.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadMenu(); }, [loadMenu]);

    const updateAvailability = async (item, isSoldOut) => {
        setUpdatingId(item.id);
        setError('');
        try {
            const response = await fetch(buildApiUrl(`/api/menu/${item.id}/availability`), {
                method: 'PUT',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ isSoldOut }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Unable to update menu availability.');
            setMenuItems(currentItems => currentItems.map(current => current.id === item.id ? { ...current, isSoldOut: data.isSoldOut } : current));
        } catch (requestError) {
            setError(requestError.message || 'Unable to update menu availability.');
        } finally {
            setUpdatingId(null);
        }
    };

    if (!['admin', 'superadmin'].includes(user?.role)) return null;

    const availableCount = menuItems.filter(item => !item.isSoldOut).length;
    const soldOutCount = menuItems.length - availableCount;

    return <main className="sold-out-page">
        <header className="sold-out-page__header">
            <div><p className="eyebrow">Operations</p><h1>Menu availability</h1><p>Switch an item off as soon as it sells out. Customers cannot add it, and orders with it in a basket are stopped at checkout.</p></div>
            <button type="button" className="sold-out-refresh" onClick={loadMenu} disabled={loading}><FaSyncAlt /> Refresh</button>
        </header>
        <div className="sold-out-summary"><span><FaCheckCircle /> {availableCount} available</span><span><FaBan /> {soldOutCount} sold out</span></div>
        {error && <p className="sold-out-error" role="alert">{error}</p>}
        {loading ? <p className="sold-out-empty">Loading menu availability...</p> : <section className="sold-out-list" aria-label="Menu availability">
            {menuItems.map(item => <article className="sold-out-item" key={item.id}>
                <img src={/^https?:\/\//i.test(item.image) ? item.image : `${process.env.PUBLIC_URL}/${String(item.image || '').replace(/^\/+/, '')}`} alt="" />
                <div className="sold-out-item__copy"><h2>{item.name}</h2><p>{item.category}</p></div>
                <label className="availability-toggle">
                    <input type="checkbox" checked={Boolean(item.isSoldOut)} disabled={updatingId === item.id} onChange={event => updateAvailability(item, event.target.checked)} />
                    <span aria-hidden="true" />
                    <b>{item.isSoldOut ? 'Sold out' : 'Available'}</b>
                </label>
            </article>)}
        </section>}
    </main>;
}

export default SoldOutPage;
